import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { TextArea } from "../../../BP2Components";
import { isFieldDisabled, toDisplayString } from "./FormFIeldsV3Decorators";
import { useDebouncedCallback } from "use-debounce";

import "../formsV3.less";
import { useFieldController } from "../../../../util/useFieldController";

const MAX_ROWS = 6;

// "auto" is not CSS-animatable so the browser snaps to the rows=1 minimum
// before measuring scrollHeight — avoids the 0px transition glitch on shrink
const autoGrow = (el, maxHeight, lastScrollHeightRef) => {
   if (!el || !maxHeight) return;
   el.style.height = "auto";
   const scrollHeight = el.scrollHeight;
   el.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
   el.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
   if (lastScrollHeightRef) lastScrollHeightRef.current = scrollHeight;
};

export const AutoGrowTextArea = ({
   value,
   onChange,
   disabled,
   placeholder,
   style,
   onKeyDown,
   className,
}) => {
   const textareaRef = useRef(null);
   const maxHeightRef = useRef(0);
   const lastScrollHeightRef = useRef(0);

   // useCallback prevents the callback ref from re-running on every render
   const refCallback = useCallback((el) => {
      textareaRef.current = el;
      if (!el) return;
      const computedStyle = getComputedStyle(el);
      const lineHeight =
         parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.4;
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
      maxHeightRef.current = lineHeight * MAX_ROWS + paddingTop + paddingBottom;
   }, []);

   useLayoutEffect(() => {
      autoGrow(textareaRef.current, maxHeightRef.current, lastScrollHeightRef);
   }, [value]);

   return (
      <TextArea
         ref={refCallback}
         rows={1}
         disabled={disabled}
         className={className}
         value={value}
         placeholder={placeholder}
         onChange={onChange}
         onKeyDown={onKeyDown}
         style={style}
      />
   );
};

const FormV3TextAreaField = ({ field, formGUID, onValueChange }) => {
   const [localValue, setLocalValue] = useState(() => toDisplayString(field?.value));
   const isTypingRef = useRef(false);

   const {
      value: ctxValue,
      setValue: setCtxValue,
      fieldInfo,
   } = useFieldController({
      field,
      formGUID,
      onValueChange,
   });

   const commitDebounced = useDebouncedCallback((val) => {
      isTypingRef.current = false;
      setCtxValue(val);
   }, 500);

   const handleChange = (e) => {
      isTypingRef.current = true;
      setLocalValue(e.target.value);
      commitDebounced(e.target.value);
   };

   useEffect(() => {
      if (isTypingRef.current) return;
      setLocalValue(toDisplayString(ctxValue));
   }, [ctxValue]);

   return (
      <span className="v3-field-element">
         <AutoGrowTextArea
            className={`v3-field id-${field.id}`}
            value={localValue}
            disabled={isFieldDisabled(field)}
            placeholder={field.placeholder}
            onChange={handleChange}
            onKeyDown={(e) => {
               // Prevent Enter from triggering questionnaire page navigation
               if (e.key === "Enter") e.stopPropagation();
            }}
            style={fieldInfo ? { borderColor: `var(--form-warnings)` } : undefined}
         />
         <span className="p2 dev-info">{field.id}</span>
         {fieldInfo ? <div className="validation-error">{fieldInfo}</div> : null}
      </span>
   );
};

export default FormV3TextAreaField;
